import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  Building2, 
  Store, 
  ChevronRight, 
  ChevronDown, 
  MapPin,
  Users,
  Globe,
  ArrowLeft
} from 'lucide-react';
import merchantService, { Organization, Merchant } from '@/services/merchantService';

interface HierarchyNode {
  merchant: Merchant;
  children: HierarchyNode[];
  level: number;
}

const OrganizationHierarchy: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const organizationId = id;
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [hierarchy, setHierarchy] = useState<HierarchyNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    console.log('OrganizationHierarchy mounted, organizationId:', organizationId);
    if (organizationId) {
      console.log('OrganizationId is valid, loading data');
      loadOrganization();
      loadHierarchy();
    } else {
      console.error('OrganizationId is undefined or null');
    }
  }, [organizationId]);

  // Safety timeout to ensure loading state doesn't get stuck
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        console.warn('Loading timeout - forcing loading to false');
        console.warn('Current state:', { organizationId, loading, hierarchy: hierarchy.length, organization: organization?.name });
        setLoading(false);
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timer);
  }, [loading, organizationId, hierarchy.length, organization]);

  const loadOrganization = async () => {
    try {
      console.log('Loading organization for ID:', organizationId);
      const org = await merchantService.getOrganizationById(organizationId!);
      console.log('Organization loaded:', org);
      setOrganization(org);
    } catch (err) {
      console.error('Error loading organization:', err);
      // Don't set error here to avoid blocking hierarchy display
    }
  };

  const loadHierarchy = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading hierarchy for organization:', organizationId);
      
      // Use the new hierarchy endpoint
      const response = await merchantService.getOrganizationHierarchy(organizationId!);
      console.log('Hierarchy API response:', response);
      console.log('Response hierarchy type:', typeof response.hierarchy);
      console.log('Response hierarchy length:', response.hierarchy?.length);
      console.log('First item in hierarchy:', response.hierarchy?.[0]);
      
      // Check if the API returns hierarchical data (with children) or flat data
      const isHierarchical = response.hierarchy && response.hierarchy.length > 0 &&
                            response.hierarchy[0].children !== undefined;
      
      let hierarchyData: HierarchyNode[];
      
      if (isHierarchical) {
        console.log('API returned hierarchical data, converting to HierarchyNode format');
        // API already returns hierarchical data, convert to our format
        const convertToHierarchyNodes = (nodes: any[], level = 0): HierarchyNode[] => {
          return nodes.map(node => ({
            merchant: {
              ...node,
              // Remove children from merchant object to avoid duplication
              children: undefined
            },
            children: convertToHierarchyNodes(node.children || [], level + 1),
            level
          }));
        };
        hierarchyData = convertToHierarchyNodes(response.hierarchy || []);
      } else {
        console.log('API returned flat data, building hierarchy');
        // Transform flat API response to our hierarchy structure
        const buildHierarchy = (merchants: any[], parentId: string | null = null, level = 0): HierarchyNode[] => {
          // Handle null/undefined parentId comparison properly
          return merchants
            .filter(merchant => {
              // Both null or both undefined
              if (!merchant.parent_store_id && !parentId) return true;
              // String comparison
              return merchant.parent_store_id === parentId;
            })
            .map(merchant => ({
              merchant,
              children: buildHierarchy(merchants, merchant.merchant_id, level + 1),
              level
            }));
        };
        hierarchyData = buildHierarchy(response.hierarchy || []);
      }
      
      console.log('Built hierarchy data:', hierarchyData);
      console.log('Hierarchy data length:', hierarchyData.length);
      setHierarchy(hierarchyData);
      
      // Expand first level by default
      const firstLevelIds = hierarchyData.map(node => node.merchant.merchant_id);
      setExpandedNodes(new Set(firstLevelIds));
    } catch (err: any) {
      console.error('Error loading hierarchy:', err);
      console.error('Error details:', err.message, err.stack);
      setError(err.response?.data?.error || 'Erreur lors du chargement de la hiérarchie');
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const toggleNode = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const renderHierarchyNode = (node: HierarchyNode) => {
    const isExpanded = expandedNodes.has(node.merchant.merchant_id);
    const hasChildren = node.children.length > 0;
    
    return (
      <div key={node.merchant.merchant_id} className="ml-6">
        <div className="flex items-center py-2 hover:bg-gray-50 rounded-lg">
          <div className="flex items-center flex-1">
            <div style={{ marginLeft: `${node.level * 24}px` }} className="flex items-center">
              {hasChildren ? (
                <button
                  onClick={() => toggleNode(node.merchant.merchant_id)}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              ) : (
                <div className="w-6"></div>
              )}
              
              <div className="ml-2 flex items-center">
                <Store className="h-5 w-5 text-blue-500 mr-2" />
                <div>
                  <div className="font-medium text-gray-900">
                    {node.merchant.business_name}
                    {node.merchant.store_code && (
                      <span className="ml-2 text-sm text-gray-500">({node.merchant.store_code})</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {node.merchant.business_type}
                    {node.merchant.org_region_name && (
                      <span className="ml-2">
                        <MapPin className="h-3 w-3 inline mr-1" />
                        Région: {node.merchant.org_region_name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <span className={`px-2 py-1 text-xs rounded-full ${node.merchant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {node.merchant.is_active ? 'Actif' : 'Inactif'}
            </span>
            <Link
              to={`/merchants/${node.merchant.merchant_id}`}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Voir détails
            </Link>
          </div>
        </div>
        
        {isExpanded && hasChildren && (
          <div>
            {node.children.map(child => renderHierarchyNode(child))}
          </div>
        )}
      </div>
    );
  };

  const renderStats = () => {
    const countStores = (nodes: HierarchyNode[]): number => {
      return nodes.reduce((total, node) => {
        return total + 1 + countStores(node.children);
      }, 0);
    };

    const countActiveStores = (nodes: HierarchyNode[]): number => {
      return nodes.reduce((total, node) => {
        const activeSelf = node.merchant.is_active ? 1 : 0;
        return total + activeSelf + countActiveStores(node.children);
      }, 0);
    };

    const totalStores = countStores(hierarchy);
    const activeStores = countActiveStores(hierarchy);
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Organisation</p>
              <p className="text-lg font-semibold">{organization?.name || 'N/D'}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Store className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Total Magasins</p>
              <p className="text-lg font-semibold">{totalStores}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Magasins Actifs</p>
              <p className="text-lg font-semibold">{activeStores} ({totalStores > 0 ? Math.round((activeStores / totalStores) * 100) : 0}%)</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Erreur</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/organizations"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour aux organisations
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            <Globe className="h-6 w-6 inline mr-2 text-blue-600" />
            Hiérarchie de {organization?.name || 'l\'organisation'}
          </h1>
          <p className="text-gray-600 mt-1">
            Vue hiérarchique de tous les magasins de cette organisation
          </p>
        </div>
        
        <div className="flex space-x-3">
          <Link
            to={`/organizations/${organizationId}`}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Détails de l'organisation
          </Link>
          <Link
            to={`/merchants/new?organization=${organizationId}`}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Ajouter un magasin
          </Link>
        </div>
      </div>

      {/* Stats */}
      {renderStats()}

      {/* Hierarchy Tree */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Structure hiérarchique
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Cliquez sur les flèches pour développer/réduire les niveaux
          </p>
        </div>
        
        <div className="px-4 py-5 sm:p-6">
          {hierarchy.length === 0 ? (
            <div className="text-center py-12">
              <Store className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun magasin dans cette organisation</h3>
              <p className="text-gray-500 mb-4">
                Cette organisation n'a pas encore de magasins associés.
                {organizationId && (
                  <div className="mt-2 text-sm">
                    <p>ID de l'organisation : {organizationId}</p>
                    <p>Nœuds de la hiérarchie : {hierarchy.length}</p>
                    <p>Organisation chargée : {organization ? 'Oui' : 'Non'}</p>
                  </div>
                )}
              </p>
              <Link
                to={`/merchants/new?organization=${organizationId}`}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Store className="h-4 w-4 mr-2" />
                Ajouter le premier magasin
              </Link>
            </div>
          ) : (
            <div className="space-y-1">
              {hierarchy.map(node => renderHierarchyNode(node))}
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Légende</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center">
            <Store className="h-4 w-4 text-blue-500 mr-2" />
            <span className="text-sm text-gray-600">Magasin</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 border border-gray-300 rounded mr-2"></div>
            <span className="text-sm text-gray-600">Niveau hiérarchique</span>
          </div>
          <div className="flex items-center">
            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full mr-2">Actif</span>
            <span className="text-sm text-gray-600">Magasin actif</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationHierarchy;